import { DialogContent, Switch } from '@mui/material';
import { TextSpan } from '../../Atoms/Text';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { Account } from '../../../types';

export interface SettingsModalMainProps {
  username: string;
  columnsCustom: boolean;
  widgetsCustom: boolean;
  subAccountsCustom: boolean;
  twoFactorAuth: boolean;
  subAccounts?: Account[];
  onEditColumns: () => void;
  onEditWidgets: () => void;
  onEditSubAccounts: () => void;
  onTwoFactorAuthChange: () => void;
  onCancel: () => void;
  onSave: () => void;
  saveDisabled: boolean;
}

export const SettingsModalMain = ({
  columnsCustom,
  username,
  widgetsCustom,
  subAccountsCustom,
  twoFactorAuth,
  subAccounts,
  onEditColumns,
  onEditWidgets,
  onEditSubAccounts,
  onTwoFactorAuthChange,
  onCancel,
  onSave,
  saveDisabled
}: SettingsModalMainProps) => {
  return (
    <>
      <DialogContent>
        <div className="flex flex-col px-8 py-4 max-w-xl m-auto justify-center">
          <div className="border-b-2 w-full mb-4">
            <TextSpan
              size="xl"
              dataTestId="account-customizations-title">
              Customizations for {username}
            </TextSpan>
          </div>
          <>
            <div className="flex mt-2">
              <div className="grow">
                <TextSpan
                  className="flex items-center"
                  color="grey10"
                  size="lg">
                  Table Columns:
                </TextSpan>
              </div>
              <div className="flex-none">
                <TextSpan
                  className="flex items-center"
                  color="grey10"
                  size="sm">
                  {columnsCustom ? 'Custom' : 'Default'}
                </TextSpan>
              </div>
              <div className="flex-none">
                <button
                  className="mx-2 flex items-center text-sm text-indigo hover:text-indigo-mid"
                  onClick={onEditColumns}>
                  Edit
                </button>
              </div>
            </div>
            <div className="border-b-2 pb-3.5 mb-3.5">
              <TextSpan
                color="grey5"
                dataTestId="table-columns-title"
                size="xs">
                Set the default order and visibility of columns for this user.
              </TextSpan>
            </div>
          </>
          <>
            <div className="flex mt-2">
              <div className="grow">
                <TextSpan
                  className="flex items-center"
                  color="grey10"
                  size="lg">
                  Widgets:
                </TextSpan>
              </div>
              <div className="flex-none">
                <TextSpan
                  className="flex items-center"
                  color="grey10"
                  size="sm">
                  {widgetsCustom ? 'Custom' : 'Default'}
                </TextSpan>
              </div>
              <div className="flex-none">
                <button
                  className="mx-2 flex items-center text-sm text-indigo hover:text-indigo-mid"
                  onClick={onEditWidgets}>
                  Edit
                </button>
              </div>
            </div>
            <div className="border-b-2 pb-3.5 mb-2">
              <TextSpan
                color="grey5"
                dataTestId="table-columns-title"
                size="xs">
                Set the default order and visibility of widgets for this user.
              </TextSpan>
            </div>
          </>
          {subAccounts != undefined && subAccounts.length > 0 && (
            <>
              <div className="flex mt-2">
                <div className="grow">
                  <TextSpan
                    className="flex items-center"
                    color="grey10"
                    size="lg">
                    Sub-Accounts:
                  </TextSpan>
                </div>
                <div className="flex-none cursor-default">
                  <TextSpan
                    className="flex items-center"
                    color="grey10"
                    size="sm">
                    {subAccountsCustom ? 'Custom' : 'Default'}
                  </TextSpan>
                </div>
                <div className="flex-none">
                  <button
                    className="mx-2 flex items-center text-sm text-indigo hover:text-indigo-mid"
                    onClick={onEditSubAccounts}>
                    Edit
                  </button>
                </div>
              </div>
              <div className="border-b-2 pb-3.5 mb-2">
                <TextSpan
                  color="grey5"
                  dataTestId="table-columns-title"
                  size="xs">
                  Set the default sub-accounts for this user.
                </TextSpan>
              </div>
            </>
          )}
          <>
            <div className="flex mt-2">
              <div className="grow">
                <TextSpan
                  className="flex items-center"
                  color="grey10"
                  size="lg">
                  Two-Factor Authentication
                </TextSpan>
              </div>
              <div className="flex-none">
                <Switch
                  sx={{
                    '.Mui-checked': { color: '#3B6FE8 !important' },
                    '.MuiSwitch-track': { backgroundColor: '#878EBE !important' }
                  }}
                  size="small"
                  checked={twoFactorAuth}
                  onChange={onTwoFactorAuthChange}
                />
              </div>
            </div>
            <div className="pb-2">
              <TextSpan
                color="grey5"
                size="xs">
                Enabled by default, disabling removes the 2FA requirement
              </TextSpan>
            </div>
          </>
        </div>
      </DialogContent>
      <div className="flex mx-4 mb-6 pr-5 justify-end space-x-4">
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={onCancel}>
          Cancel
        </button>
        <button
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          onClick={onSave}
          disabled={saveDisabled}>
          Save
        </button>
      </div>
    </>
  );
};
